import NumberFlow from "@number-flow/react";
import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

// Digit-shift duration for each value tick. Tuned so the green flashes
// ON together with the count-up at the tick moment, then snaps off
// mid-animation while the digits keep settling — reads as a crisp
// "live update" flash rather than two co-resolving animations.
const TICK_ANIMATION_TIMING = {
    duration: 600,
    easing: "ease-out"
} as const;

type TickerVariant =
    | "hero"
    | "h1"
    | "h2"
    | "h3"
    | "body"
    | "small"
    | "fine-print"
    | "label"
    | "number"
    | "number-lg";

export interface PendingEarningsTickerProps {
    /** The live accrued value to display. The caller owns the accrual
     *  clock — apps/main computes it from `useDistributions` + APY;
     *  the sim driver ticks it up on a timer. This component is purely
     *  presentational: it renders whatever `value` it's handed via the
     *  dual-NumberFlow count-up below. */
    value: number;
    /** When `false`, renders a static `{prefix}0.00` placeholder
     *  (loading state) instead of the live ticker. */
    isLoaded: boolean;
    color?: string;
    prefix?: string;
    /** Text variant — controls font/size. Default: `number`. */
    variant?: TickerVariant;
    /** Tailwind classes forwarded to the wrapping Text element. */
    className?: string;
}

/**
 * Presentational pending-earnings ticker — animates a live accrued
 * value via NumberFlow with sub-cent precision (six decimal places
 * under $100, four above). Mechanism-only / SDK-free: the caller owns
 * the accrual clock and feeds the current `value`.
 *
 *   - apps/main: `RetailPendingEarningsTicker` (data wrapper) computes
 *     the live value from `useDistributions` + APY and renders this.
 *   - sim: `YaasEarningsSim` ticks a mock accrual up on a timer and
 *     renders this.
 *
 * Keeps the dual-NumberFlow render so the main value and the extended
 * fractional cents each animate on their own digit slots.
 */
export function PendingEarningsTicker({
    value,
    isLoaded,
    color = "rgb(var(--color-foreground-base))",
    prefix = "$",
    variant = "number",
    className
}: PendingEarningsTickerProps): React.ReactElement {
    // Defensive non-negative floor — any negative input (stale base, a
    // bad date row) would produce the dual-NumberFlow sign-collision
    // render. Render zero rather than a garbled negative.
    const safeValue = Math.max(0, value);
    const mainValue = Math.floor(safeValue * 100) / 100;
    // Six decimals total: two from `mainValue` (the dollars/cents
    // NumberFlow) plus four here. Sub-$100 values used to get six here
    // — eight total — which was finer than the figure needs to be and
    // made small values read as noise. Precision is uniform now
    // regardless of magnitude.
    const fractionalDigits = 4;
    const fractionalCents = Math.floor((safeValue * 1_000_000) % 10_000);

    if (!isLoaded) {
        return (
            <Text variant={variant} className={className} style={{ color }}>
                {prefix}0.00
            </Text>
        );
    }

    return (
        <>
            {/* Style: NumberFlow exposes `::part(symbol)` for separators
                (decimal point, thousands group). The host element uses
                `font-numeric-mono` so digit slots are equal-width
                (NumberFlow's per-digit animation needs that), but we
                override `symbol` parts back to proportional
                `font-numeric` (APK Narrative) so `.` and `,` read
                narrow instead of occupying a full digit slot. */}
            <style>
                {`
                    number-flow-react.numflow-mono::part(symbol) {
                        font-family: var(--font-numeric);
                    }
                `}
            </style>
            <Text
                variant={variant}
                // Override the variant's proportional `font-numeric`
                // with Mono for digit-width stability during the
                // count-up animation. The `numflow-mono` class is the
                // hook the `::part(symbol)` selector above keys off.
                className={`${className ?? ""} font-numeric-mono tracking-[-0.1em]`}
                style={{ color }}
            >
                {prefix}
                <NumberFlow
                    className="numflow-mono"
                    value={mainValue}
                    format={{
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    }}
                    transformTiming={TICK_ANIMATION_TIMING}
                    spinTiming={TICK_ANIMATION_TIMING}
                    willChange
                />
                <NumberFlow
                    className="numflow-mono"
                    value={fractionalCents}
                    format={{
                        minimumIntegerDigits: fractionalDigits,
                        useGrouping: false
                    }}
                    transformTiming={TICK_ANIMATION_TIMING}
                    spinTiming={TICK_ANIMATION_TIMING}
                    willChange
                />
            </Text>
        </>
    );
}
